<script setup>
import { computed, inject } from 'vue';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const event = inject('tiqCurrentEvent', null);
// A visitor never meets the slot's placeholder: with no real event
// to render it draws nothing, and only the editor keeps the stand-in.
const editing = inject('tiqEditorCanvas', false);
// The template-aware registry, so a Trailhead page pours Trailhead
// blocks into the slot rather than Classic ones.
const components = inject('tiqBlockComponents', null);
function componentFor(type) { return components && components[type]; }

const blocks = computed(() => (Array.isArray(event?.body) ? event.body : []));
const widthClass = computed(() => `is-w-${props.config.width || 'medium'}`);
</script>

<template>
    <!-- A real event with no detail blocks collapses to nothing, which
         is better for a visitor than a "no content yet" notice. The
         editor canvas, which has no event, keeps the placeholder so
         the template author can see the slot is there. -->
    <section v-if="event ? blocks.length : editing"
             :id="config.anchor_id || null"
             class="th-block th-sec th-ebody"
             :class="widthClass"
             :style="styleVars(config)">
        <div class="th-wrap">
            <div class="th-ebody__inner">
                <template v-for="block in blocks" :key="block.id">
                    <component v-if="componentFor(block.type)"
                               :is="componentFor(block.type)"
                               :config="block.config"
                               :accent="accent"
                               :org="org"
                               :site_data="site_data" />
                </template>
                <p v-if="!event" class="th-ebody__empty">
                    Event body appears here when the template renders a real event.
                </p>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-ebody__inner { margin: 0 auto; }
.th-ebody.is-w-narrow .th-ebody__inner { max-width: 600px; }
.th-ebody.is-w-medium .th-ebody__inner { max-width: 760px; }
.th-ebody.is-w-wide .th-ebody__inner { max-width: 1000px; }
.th-ebody.is-w-full .th-ebody__inner { max-width: none; }
/* A nested block brings its own section padding and gutter. Inside
   the slot the body column has already paid for both, so a child that
   kept them would be indented twice and spaced like a page. */
.th-ebody__inner :deep(.th-block) { background: transparent; }
.th-ebody__inner :deep(.th-sec),
.th-ebody__inner :deep(.th-band) { padding-top: 0; padding-bottom: 22px; }
.th-ebody__inner :deep(.th-wrap) { padding-left: 0; padding-right: 0; max-width: none; }
.th-ebody__empty {
    margin: 0;
    padding: 32px;
    text-align: center;
    font-size: 15px;
    color: var(--th-faint);
    border: 1px dashed var(--th-field);
    border-radius: var(--r);
    background: rgba(0, 0, 0, .02);
}
</style>
